/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React from 'react';
import styled from 'styled-components';
import { useThemeContext } from 'components-stateless/Theme/ThemeContext';
import type { ButtonProps } from './types';
import TempIconSVG from './Colour=White, Size=20.svg?asComponent';
import { COLOURS_AA } from '../Colours';
import ButtonSpinner from '../Spinner/ButtonSpinner';
import { BtnAccessibleAA, BtnSmall, BtnXSmall } from '../Fonts';

const ButtonBaseSquare = styled.button<
  BtnColorScheme & {
    border?: React.CSSProperties['border'];
    filter?: React.CSSProperties['filter'];
    paddingBottom?: React.CSSProperties['paddingBottom'];
    paddingLeft?: React.CSSProperties['paddingLeft'];
    paddingRight?: React.CSSProperties['paddingRight'];
    paddingTop?: React.CSSProperties['paddingTop'];
    iconHeight?: React.CSSProperties['height'];
    buttonHeight?: React.CSSProperties['height'];
    itemOpacity?: React.CSSProperties['opacity'];
  }
>`
  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  height: ${({ buttonHeight }) => buttonHeight};
  position: relative;
  white-space: nowrap;

  /* Dynamic padding */
  padding-left: ${({ paddingLeft }) => paddingLeft};
  padding-right: ${({ paddingRight }) => paddingRight};
  padding-top: ${({ paddingTop }) => paddingTop};
  padding-bottom: ${({ paddingBottom }) => paddingBottom};

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;

  filter: ${({ filter }) => filter || 'initial'};
  border: ${({ border }) => border || 'initial'};
  border-radius: 4px;

  color: ${({ textColorDefault }) => textColorDefault};
  background: ${({ backgroundDefault }) => backgroundDefault};

  &:hover > * > path,
  &:hover,
  &:focus > * > path,
  &:focus {
    fill: ${({ textColorHover }) => textColorHover};
    color: ${({ textColorHover }) => textColorHover};
    background: ${({ backgroundHover }) => backgroundHover};
  }
  &:active > * > path,
  &:active {
    fill: ${({ textColorActive }) => textColorActive};
    color: ${({ textColorActive }) => textColorActive};
    background: ${({ backgroundActive }) => backgroundActive};
  }
  &:disabled,
  &:disabled > * > path {
    background: ${({ backgroundDisabled }) => backgroundDisabled};
    color: ${({ textColorDisabled }) => textColorDisabled};
    fill: ${({ textColorDisabled }) => textColorDisabled};
    cursor: default;
  }
  > * > path {
    fill: ${({ textColorDefault }) => textColorDefault};
  }

  svg {
    height: ${({ iconHeight }) => iconHeight};
    width: ${({ iconHeight }) => iconHeight};
  }

  /* Loading spinner */
  > * {
    opacity: ${({ itemOpacity }) => itemOpacity};
  }
`;

const ButtonBaseRound = styled.button<{
  backgroundDisabled: React.CSSProperties['background'];
  backgroundDefault: React.CSSProperties['background'];
  backgroundHover: React.CSSProperties['background'];
  backgroundActive: React.CSSProperties['background'];
  border?: React.CSSProperties['border'];
  filter?: React.CSSProperties['filter'];
  textColorDefault: React.CSSProperties['color'];
  textColorDisabled: React.CSSProperties['color'];
  iconHeight?: React.CSSProperties['height'];
  buttonHeight?: React.CSSProperties['height'];
  itemOpacity?: React.CSSProperties['opacity'];
}>`
  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 4px;

  width: ${({ buttonHeight }) => buttonHeight};
  height: ${({ buttonHeight }) => buttonHeight};

  position: relative;

  cursor: pointer;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;

  filter: ${({ filter }) => filter || 'initial'};
  border: ${({ border }) => border || 'initial'};
  border-radius: 64px;

  background: ${({ backgroundDefault }) => backgroundDefault};
  &:hover,
  &:focus {
    background: ${({ backgroundHover }) => backgroundHover};
  }
  &:active {
    background: ${({ backgroundActive }) => backgroundActive};
  }
  &:disabled,
  &:disabled > * > path {
    background: ${({ backgroundDisabled }) => backgroundDisabled};
    color: ${({ textColorDisabled }) => textColorDisabled};
    fill: ${({ textColorDisabled }) => textColorDisabled};
    cursor: default;
  }
  > * > path {
    fill: ${({ textColorDefault }) => textColorDefault};
  }

  svg {
    height: ${({ iconHeight }) => iconHeight};
    width: ${({ iconHeight }) => iconHeight};
  }

  /* Loading spinner */
  > * {
    opacity: ${({ itemOpacity }) => itemOpacity};
  }
`;

const SpinnerWrap = styled.div`
  align-items: center;
  background: inherit;
  border-radius: 4px;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  opacity: 1;
  position: absolute;
  right: 0;
  top: 0;
`;

const TempIcon = <TempIconSVG />;

type BtnColorScheme = {
  backgroundDefault: React.CSSProperties['background'];
  backgroundHover: React.CSSProperties['background'];
  backgroundActive: React.CSSProperties['background'];
  backgroundDisabled: React.CSSProperties['background'];
  textColorDefault: React.CSSProperties['color'];
  textColorHover: React.CSSProperties['color'];
  textColorActive: React.CSSProperties['color'];
  textColorDisabled: React.CSSProperties['color'];
  border?: React.CSSProperties['border'];
};

/** Takes the button 'type', returns the colours needed for the button
 *
 * The design system contains a huge table of colours that every button type should be.
 * Changes to the design system colours should be updated in this function.
 */
const setupColourScheme = (
  buttonType: ButtonProps['buttonType'],
  WhitelabelButtonBGColor: string,
  WhitelabelButtonFontColor: string,
  allowWhitelabelling?: boolean
): BtnColorScheme => {
  if (allowWhitelabelling) {
    return {
      backgroundDefault: WhitelabelButtonBGColor,
      backgroundHover: WhitelabelButtonBGColor,
      backgroundActive: WhitelabelButtonBGColor,
      backgroundDisabled: COLOURS_AA.disabled.Disabled200,
      textColorDefault: WhitelabelButtonFontColor,
      textColorHover: WhitelabelButtonFontColor,
      textColorActive: WhitelabelButtonFontColor,
      textColorDisabled: COLOURS_AA.text.TxtDisabled,
    };
  }
  switch (buttonType) {
    case 'Primary': {
      return {
        backgroundDefault: COLOURS_AA.primary.BtnPrimaryDefault,
        backgroundHover: COLOURS_AA.primary.BtnPrimaryHover,
        backgroundActive: COLOURS_AA.primary.BtnPrimaryClick,
        backgroundDisabled: COLOURS_AA.disabled.Disabled200,
        textColorDefault: COLOURS_AA.text.WhiteTxt,
        textColorHover: COLOURS_AA.text.WhiteTxt,
        textColorActive: COLOURS_AA.text.WhiteTxt,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }

    case 'Secondary': {
      return {
        backgroundDefault: COLOURS_AA.secondary.BtnSecondaryDefault,
        backgroundHover: COLOURS_AA.secondary.BtnSecondaryHover,
        backgroundActive: COLOURS_AA.secondary.BtnSecondaryClick,
        backgroundDisabled: COLOURS_AA.disabled.Disabled200,
        textColorDefault: COLOURS_AA.text.WhiteTxt,
        textColorHover: COLOURS_AA.text.WhiteTxt,
        textColorActive: COLOURS_AA.text.WhiteTxt,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }

    case 'Ghost': {
      return {
        backgroundDefault: 'initial',
        backgroundHover: COLOURS_AA.neutralBG.BgGrey300,
        backgroundActive: COLOURS_AA.neutralBG.BgGrey200,
        backgroundDisabled: COLOURS_AA.disabled.Disabled200,
        textColorDefault: COLOURS_AA.tertiaryTxt.BtnTertiaryDarkDefault,
        textColorHover: COLOURS_AA.tertiaryTxt.BtnTertiaryDarkHover,
        textColorActive: COLOURS_AA.tertiaryTxt.BtnTertiaryDarkClick,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
        border: `1px solid ${COLOURS_AA.neutralBG.BgGrey300}`,
      };
    }

    case 'Tertiary Green': {
      return {
        backgroundDefault: 'initial',
        backgroundHover: COLOURS_AA.neutralBG.BgTableCell,
        backgroundActive: 'initial',
        backgroundDisabled: 'initial',
        textColorDefault: COLOURS_AA.primary.BtnPrimaryDefault,
        textColorHover: COLOURS_AA.primary.BtnPrimaryHover,
        textColorActive: COLOURS_AA.primary.BtnPrimaryClick,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }

    case 'Tertiary Dark': {
      return {
        backgroundDefault: 'initial',
        backgroundHover: COLOURS_AA.neutralBG.BgGrey200,
        backgroundActive: 'initial',
        backgroundDisabled: 'initial',
        textColorDefault: COLOURS_AA.tertiaryTxt.BtnTertiaryDarkDefault,
        textColorHover: COLOURS_AA.tertiaryTxt.BtnTertiaryDarkHover,
        textColorActive: COLOURS_AA.tertiaryTxt.BtnTertiaryDarkClick,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }

    case 'SpecialLight': {
      return {
        backgroundDefault: COLOURS_AA.neutralBG.BgGrey250,
        backgroundHover: COLOURS_AA.neutralBG.BgGrey300,
        backgroundActive: COLOURS_AA.neutralBG.BgGrey200,
        backgroundDisabled: COLOURS_AA.disabled.Disabled200,
        textColorDefault: COLOURS_AA.text.StandardTxt,
        textColorHover: COLOURS_AA.text.StrongTxt,
        textColorActive: COLOURS_AA.text.StandardTxt,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }

    case 'Special': {
      return {
        backgroundDefault: COLOURS_AA.special.BtnSpecialDefault,
        backgroundHover: COLOURS_AA.special.BtnSpecialHover,
        backgroundActive: COLOURS_AA.special.BtnSpecialClick,
        backgroundDisabled: COLOURS_AA.disabled.Disabled200,
        textColorDefault: COLOURS_AA.neutralBG.BgGrey700,
        textColorHover: COLOURS_AA.neutralBG.BgGrey700,
        textColorActive: COLOURS_AA.neutralBG.BgGrey700,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }

    case 'Delete': {
      return {
        backgroundDefault: COLOURS_AA.delete.BtnDeleteDefault,
        backgroundHover: COLOURS_AA.delete.BtnDeleteHover,
        backgroundActive: COLOURS_AA.delete.RedDeleteClick,
        backgroundDisabled: COLOURS_AA.disabled.Disabled200,
        textColorDefault: COLOURS_AA.text.WhiteTxt,
        textColorHover: COLOURS_AA.text.WhiteTxt,
        textColorActive: COLOURS_AA.text.WhiteTxt,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }

    case 'Monochrome': {
      return {
        backgroundDefault: COLOURS_AA.neutralBG.BgWhite,
        backgroundHover: COLOURS_AA.neutralBG.BgGrey100,
        backgroundActive: COLOURS_AA.neutralBG.BgWhite,
        backgroundDisabled: COLOURS_AA.disabled.Disabled200,
        textColorDefault: COLOURS_AA.neutralBG.BgBlack,
        textColorHover: COLOURS_AA.neutralBG.BgGrey700,
        textColorActive: COLOURS_AA.neutralBG.BgBlack,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }

    default: {
      return {
        backgroundDefault: COLOURS_AA.primary.BtnPrimaryDefault,
        backgroundHover: COLOURS_AA.primary.BtnPrimaryHover,
        backgroundActive: COLOURS_AA.primary.BtnPrimaryClick,
        backgroundDisabled: COLOURS_AA.disabled.Disabled200,
        textColorDefault: COLOURS_AA.text.WhiteTxt,
        textColorHover: COLOURS_AA.text.WhiteTxt,
        textColorActive: COLOURS_AA.text.WhiteTxt,
        textColorDisabled: COLOURS_AA.text.TxtDisabled,
      };
    }
  }
};

const setupPadding = (
  iconOnly: boolean,
  iconLeft: boolean,
  iconRight: boolean,
  borderSize = 0
): {
  left: string;
  right: string;
  top: string;
  bottom: string;
} => {
  if (iconOnly) {
    return {
      left: `${4 - borderSize}px`,
      right: `${4 - borderSize}px`,
      top: `${4 - borderSize}px`,
      bottom: `${4 - borderSize}px`,
    };
  }
  if (iconLeft && iconRight) {
    return {
      left: `${6 - borderSize}px`,
      right: `${6 - borderSize}px`,
      top: `${4 - borderSize}px`,
      bottom: `${4 - borderSize}px`,
    };
  }
  if (iconLeft && !iconRight) {
    return {
      left: `${6 - borderSize}px`,
      right: `${12 - borderSize}px`,
      top: `${4 - borderSize}px`,
      bottom: `${4 - borderSize}px`,
    };
  }

  if (!iconLeft && iconRight) {
    return {
      left: `${12 - borderSize}px`,
      right: `${6 - borderSize}px`,
      top: `${4 - borderSize}px`,
      bottom: `${4 - borderSize}px`,
    };
  }

  return {
    left: `${16 - borderSize}px`,
    right: `${16 - borderSize}px`,
    top: `${4 - borderSize}px`,
    bottom: `${4 - borderSize}px`,
  };
};

/**
 * Button is part of the Design System: https://www.figma.com/file/SbamdS9x3EglDShf3f3f34/Checkbox-Design-System?node-id=363%3A7319
 *
 * Report design issues to Anthony Palaje and implementation issues to Adam Parsons
 *
 * Extends HTML Button, so you may treat <Button> like a <button>, except for a few required props.
 *
 * When using in a HTML Form, you will need to use the `type` prop if you want native browser
 * functionality for forms to work
 *
 * Icons can be left, right, both, iconOnly (no text) or none.
 *
 * @param {string} [allowWhitelabelling] - Button will use the customers whitelabelling colours from a contextprovider
 * @param {string} [buttonType] - The type of button from the Design System to display. This changes colours but doesn't change the HTMLButton type, this is done with `type` instead.
 * @param {string} [buttonSize] - The size of the button
 * @param {string} [shape] - The shape of the button, eg 'Round' or 'Square'
 * @param {string} [children] - The text to display in the button
 * @param {string} [disabled] - Whether the button is disabled
 * @param {string} [loading] - Whether the button is loading
 * @param {string} [iconLeft] - The icon (component) to display on the left.
 * @param {string} [iconRight] - The icon (component to display on the right.
 * @param {string} [iconOnly] - Whether to display an icon without any text, do not use `iconRight` or `iconLeft` with this option, it is not needed.
 * @param {string} [type] - The type of button to render in the dom, such as 'reset', 'button', or 'submit'. This is different to `buttonType`. If using `reset` or `submit`, make sure `onClick` is _not_ used
 * @param {string} [className] - A class name to apply to the button
 * @param {string} [style] - Inline styles to apply to the button
 * @param {string} [onClick] - A function to call when the button is clicked
 * @param {string} [onMouseEnter] - A function to call when the mouse enters the button
 * @param {string} [onMouseLeave] - A function to call when the mouse leaves the button
 * @param {string} [dataTestId] - The data-testid attribute for testing purposes
 *
 */
const Button = (props: ButtonProps) => {
  const {
    allowWhitelabelling,
    buttonType,
    buttonSize,
    children,
    iconLeft,
    iconOnly,
    iconRight,
    loading,
    shape,
    dataTestId,
    ...rest
  } = props;

  // Checkbox Whitelabelling is supported by the Button component
  const {
    uiControlBackgroundColor: WhitelabelButtonBGColor,
    uiControlFontColor: WhitelabelButtonFontColor,
  } = useThemeContext();

  // Set up the color scheme
  const {
    backgroundActive,
    backgroundDefault,
    backgroundHover,
    backgroundDisabled,
    textColorDefault,
    textColorHover,
    textColorActive,
    textColorDisabled,
    border,
  } = setupColourScheme(
    buttonType,
    WhitelabelButtonBGColor,
    WhitelabelButtonFontColor,
    allowWhitelabelling
  );

  const borderSize: number = (() => {
    if (typeof border === 'number') {
      return border;
    }
    if (typeof border === 'string') {
      return Number(border?.split('px')[0]);
    }
    return 0;
  })();

  const { left, right, top, bottom } = setupPadding(
    !!iconOnly,
    !!iconLeft,
    !!iconRight,
    borderSize
  );

  const filter: React.CSSProperties['filter'] = (() => {
    if (buttonType === 'Tertiary Dark' || buttonType === 'Tertiary Green') {
      return 'initial';
    }
    return 'drop-shadow(0px 2px 2px rgba(28, 100, 208, 0.08))';
  })();

  const { buttonHeight, iconHeight, ButtonFont } = (() => {
    switch (buttonSize) {
      case 'AccessibleAA':
        return {
          buttonHeight: '44px',
          iconHeight: '20px',
          ButtonFont: BtnAccessibleAA,
        };
      case 'X-Small':
        return {
          buttonHeight: '32px',
          iconHeight: '16px',
          ButtonFont: BtnXSmall,
        };
      default:
        return {
          buttonHeight: '32px',
          iconHeight: '20px',
          ButtonFont: BtnSmall,
        };
    }
  })();

  if (shape === 'Square') {
    return (
      <ButtonBaseSquare
        backgroundActive={backgroundActive}
        backgroundDefault={backgroundDefault}
        backgroundHover={backgroundHover}
        backgroundDisabled={backgroundDisabled}
        border={border}
        filter={filter}
        itemOpacity={loading ? 0 : 1}
        paddingBottom={bottom}
        paddingLeft={left}
        paddingRight={right}
        paddingTop={top}
        textColorActive={textColorActive}
        textColorDefault={textColorDefault}
        textColorHover={textColorHover}
        textColorDisabled={textColorDisabled}
        iconHeight={iconHeight}
        buttonHeight={buttonHeight}
        data-testid={dataTestId}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      >
        {iconLeft && typeof iconLeft === 'boolean' ? TempIcon : iconLeft}
        {iconOnly && typeof iconOnly === 'boolean' ? TempIcon : iconOnly}
        {!iconOnly && <ButtonFont>{children}</ButtonFont>}
        {iconRight && typeof iconRight === 'boolean' ? TempIcon : iconRight}
        {loading && (
          <SpinnerWrap>
            <ButtonSpinner />
          </SpinnerWrap>
        )}
      </ButtonBaseSquare>
    );
  }

  // Round buttons don't contain text, only one icon
  return (
    <ButtonBaseRound
      backgroundActive={backgroundActive}
      backgroundDefault={backgroundDefault}
      backgroundHover={backgroundHover}
      backgroundDisabled={backgroundDisabled}
      border={border}
      filter={filter}
      itemOpacity={loading ? 0 : 1}
      textColorDefault={textColorDefault}
      textColorDisabled={textColorDisabled}
      iconHeight={iconHeight}
      buttonHeight={buttonHeight}
      data-testid={dataTestId}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {iconOnly && <>{typeof iconOnly === 'boolean' ? TempIcon : iconOnly}</>}
      {loading && (
        <SpinnerWrap>
          <ButtonSpinner />
        </SpinnerWrap>
      )}
    </ButtonBaseRound>
  );
};

export default Button;
